const _addPath = (parent, key, _path) => {
    const path = _ensurePath(_path);
    return {
        keys: [...path.keys, key],
        parents: [...path.parents, parent],
    };
};
const _ensurePath = (path) => path || {
    keys: [],
    parents: [],
};
const _proxySetHandlerReadOnly = () => {
    throw new Error('This Proxy is read-only.');
};
const _proxyGetHandlerThrowIfKeyUnknown = (target, key, path) => {
    if (!(key in target)) {
        // Whitelist some fields that are undefined but accessed at
        // some point or another by our code.
        // TODO : find a better way to do this.
        if ([
            'toJSON',
            'Symbol(Symbol.toStringTag)',
            'constructor',
            '$typeof',
            '$$typeof',
            '@@__IMMUTABLE_ITERABLE__@@',
            '@@__IMMUTABLE_RECORD__@@',
            'then',
        ].includes(key)) {
            return true;
        }
        throw new Error(`namespace${path ? ` <${path.keys.join('.')}>` : ''} doesn't know key "${String(key)}"`);
    }
    return false;
};
const proxyAsAssigner = (spec, _obj, context, _path) => {
    const path = _path || { keys: [], parents: [] };
    const obj = proxyAsAssigner.ensureValue(_obj, spec, context, path);
    // If `_path` is provided, assign the new value to the parent object.
    if (_path) {
        const parent = _path.parents[_path.parents.length - 1];
        const key = _path.keys[_path.keys.length - 1];
        // The only case where we want to overwrite the existing value
        // is when it was a `null` assigned by `LiteralDefaultNull`, and
        // we want to set the real value instead.
        if (!(key in parent) || 'LiteralDefaultNull' in spec) {
            parent[key] = obj;
        }
    }
    // If the object is a Literal, end of the recursion.
    if ('Literal' in spec || 'LiteralDefaultNull' in spec) {
        return obj;
    }
    return new Proxy(obj, {
        get: (_, k) => {
            const key = String(k);
            let nextSpec;
            if ('Index' in spec) {
                nextSpec = spec.Index(key, context, path);
            }
            else if ('Interface' in spec) {
                if (!(key in spec.Interface)) {
                    throw new Error(`Interface has no entry "${String(key)}"`);
                }
                nextSpec = spec.Interface[key];
            }
            else {
                throw new Error('no builder');
            }
            return proxyAsAssigner(nextSpec, 
            // We use this form here instead of `obj[key]` specifically
            // to allow Assign to play well with `ProtectedIndex`, which
            // would raise an error if trying to access an undefined key.
            key in obj ? obj[key] : undefined, context, _addPath(obj, key, path));
        },
        set: _proxySetHandlerReadOnly,
    });
};
proxyAsAssigner.ensureValue = (_obj, spec, context, _path, _recursionPath) => {
    if ('Index' in spec) {
        return (_obj || spec.indexConstructor(context, _ensurePath(_path)));
    }
    else if ('Interface' in spec) {
        const obj = (_obj || {});
        Object.entries(spec.Interface).forEach(([key, nextSpec]) => {
            obj[key] = proxyAsAssigner.ensureValue(obj[key], nextSpec, context, _addPath(obj, key, _path), _addPath(obj, key, _recursionPath));
        });
        return obj;
    }
    else if ('Literal' in spec) {
        return (_obj || spec.Literal(context, _ensurePath(_path)));
    }
    else if ('LiteralDefaultNull' in spec) {
        if (!_recursionPath) {
            return (_obj ||
                spec.LiteralDefaultNull(context, _ensurePath(_path)));
        }
        else {
            return (_obj || null);
        }
    }
    else {
        throw new Error('Invalid Assigner');
    }
};
proxyAsAssigner.Interface = (a) => ({ Interface: a });
proxyAsAssigner.Index = (f, indexConstructor) => ({
    Index: f,
    indexConstructor: indexConstructor || (() => ({})),
});
proxyAsAssigner.Literal = (f) => ({
    Literal: f,
});
proxyAsAssigner.LiteralDefaultNull = (f) => ({ LiteralDefaultNull: f });
// ---------------------------- proxyAsProtectedIndex ---------------------------- //
/**
 * Helper to declare namespace objects enforcing stricter access rules.
 * Specifically, it forbids :
 * - reading an unknown property.
 * - trying to overwrite an existing property.
 */
const proxyAsProtectedIndex = (namespace, path) => {
    return new Proxy(namespace, {
        get: (target, k) => {
            const key = String(k);
            if (_proxyGetHandlerThrowIfKeyUnknown(target, key, path)) {
                return undefined;
            }
            return target[key];
        },
        set: (target, k, newValue) => {
            const key = _trimDollarKey(String(k));
            if (target.hasOwnProperty(key)) {
                throw new Error(`Key "${String(key)}" is protected and cannot be overwritten.`);
            }
            else {
                target[key] = newValue;
            }
            return newValue;
        },
    });
};
// ---------------------------- proxyAsReadOnlyIndex ---------------------------- //
/**
 * Helper to declare namespace objects enforcing stricter access rules.
 * Specifically, it forbids :
 * - reading an unknown property.
 * - writing to a property.
 */
const proxyAsReadOnlyIndex = (namespace, path) => {
    return new Proxy(namespace, {
        get: (target, k) => {
            const key = String(k);
            if (_proxyGetHandlerThrowIfKeyUnknown(target, key, path)) {
                return undefined;
            }
            const value = target[key];
            if (typeof value === 'object' && value !== null) {
                return proxyAsReadOnlyIndex(value, _addPath(target, key, path));
            }
            else {
                return value;
            }
        },
        set: _proxySetHandlerReadOnly,
    });
};
// ---------------------------- proxyAsReadOnlyIndexWithDollarKeys ---------------------------- //
/**
 * Helper to declare namespace objects enforcing stricter access rules.
 * Specifically :
 * - it is read only
 * - it throws an error when trying to read an unknown property.
 * - allows to access properties starting with a number by prepending a `$`.
 *      This is convenient to access portlets by their id without using
 *      indexing syntax, for example : `namespace.$0` instead of `namespace['0']`.
 */
const proxyAsReadOnlyIndexWithDollarKeys = (namespace, nodeId, name) => {
    return new Proxy(namespace, {
        get: (target, k) => {
            const key = _trimDollarKey(String(k));
            if (_proxyGetHandlerThrowIfKeyUnknown(target, key, {
                parents: [target],
                keys: [nodeId, name],
            })) {
                return undefined;
            }
            return target[key];
        },
        set: _proxySetHandlerReadOnly,
    });
};
const _trimDollarKey = (key) => {
    const match = /\$(.*)/.exec(key);
    if (!match) {
        return key;
    }
    else {
        return match[1];
    }
};

export { _proxyGetHandlerThrowIfKeyUnknown, proxyAsAssigner, proxyAsProtectedIndex, proxyAsReadOnlyIndex, proxyAsReadOnlyIndexWithDollarKeys };
